/* The hifi component library: displays (glass, not clickable), controls (raised keys), indicators (lamp + word).
   The look is fixed by the tokens; props only carry state. See docs/player-design. */
import { For, JSX, Show, createEffect, createSignal, onCleanup, splitProps } from 'solid-js';
import { vuAngle } from '../audio/meters';

export function Faceplate(props: { as?: 'header' | 'section' | 'main'; class?: string; label?: string; children: JSX.Element }) {
  const cls = () => `faceplate ${props.class ?? ''}`;
  if (props.as === 'header') return <header class={cls()} aria-label={props.label}>{props.children}</header>;
  if (props.as === 'main') return <main class={cls()} aria-label={props.label}>{props.children}</main>;
  return <section class={cls()} aria-label={props.label}>{props.children}</section>;
}

export function Glass(props: { class?: string; children: JSX.Element; role?: JSX.HTMLAttributes<HTMLDivElement>['role']; 'aria-label'?: string }) {
  return <div class={`glass ${props.class ?? ''}`} role={props.role} aria-label={props['aria-label']}>{props.children}</div>;
}

export type Word = { text: string; on: boolean; red?: boolean };

export function Annunciators(props: { words: Word[]; class?: string }) {
  return (
    <div class={`flex justify-between gap-4 text-[15px] tracking-[0.08em] ${props.class ?? ''}`}>
      <For each={props.words}>
        {(w) => <span class={w.on ? (w.red ? 'vfd-red' : 'vfd') : 'vfd-off'}>{w.text}</span>}
      </For>
    </div>
  );
}

/** One line of VFD text. Lines longer than the window scroll once, pause 4 s, scroll again; never wrap. */
export function Readout(props: { size: 'xl' | 'l' | 'm'; tone?: 'lit' | 'muted' | 'red'; text: string; live?: boolean; class?: string }) {
  let outer!: HTMLDivElement;
  let inner!: HTMLSpanElement;
  const [shift, setShift] = createSignal(0);
  const [dur, setDur] = createSignal(0);
  const sizes = { xl: 'text-[34px] leading-[38px] sm:text-[48px] sm:leading-[52px]', l: 'text-[22px] leading-[26px]', m: 'text-[15px] leading-[20px] tracking-[0.08em]' };
  const tone = () => (props.tone === 'muted' ? 'vfd-muted' : props.tone === 'red' ? 'vfd-red' : props.size === 'xl' ? 'vfd-xl' : 'vfd');

  createEffect(() => {
    props.text;
    setShift(0);
    setDur(0);
    let timer: number | undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cycle = () => {
      const over = inner.scrollWidth - outer.clientWidth;
      if (over <= 0 || reduce) return;
      const secs = over / 40;
      timer = window.setTimeout(() => {
        setDur(secs); setShift(-over);
        timer = window.setTimeout(() => { setDur(0); setShift(0); cycle(); }, secs * 1000 + 4000);
      }, 4000);
    };
    queueMicrotask(cycle);
    onCleanup(() => clearTimeout(timer));
  });

  return (
    <div ref={outer} class={`overflow-hidden whitespace-nowrap ${sizes[props.size]} ${props.class ?? ''}`} aria-live={props.live ? 'polite' : undefined}>
      <span ref={inner} class={`inline-block ${tone()}`} style={{ transform: `translateX(${shift()}px)`, transition: dur() ? `transform ${dur()}s linear` : 'none' }}>
        {props.text}
      </span>
    </div>
  );
}

export function Lamp(props: { tone: 'off' | 'green' | 'red' | 'warn'; word: string }) {
  return (
    <div class="flex items-center gap-1.5">
      <span class={`lamp ${props.tone === 'off' ? '' : props.tone}`} aria-hidden="true" />
      <span class="legend" style={{ color: props.tone === 'off' ? 'var(--legend-muted)' : 'var(--legend)' }}>{props.word}</span>
      <span class="sr-only">{props.tone === 'off' ? 'off' : 'on'}</span>
    </div>
  );
}

/** Transport symbols as on the hardware, drawn as SVG so they never depend on a font having the glyph. */
export function Sym(props: { name: 'play' | 'pause' | 'stop' | 'skip' }) {
  const paths = {
    play: 'M5 3 L13 8 L5 13 Z',
    pause: 'M4 3 H7 V13 H4 Z M9 3 H12 V13 H9 Z',
    stop: 'M4 4 H12 V12 H4 Z',
    skip: 'M2 3 L8 8 L2 13 Z M8 3 L14 8 L8 13 Z M14 3 H15.5 V13 H14 Z',
  };
  return <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" class="inline-block"><path d={paths[props.name]} fill="currentColor" /></svg>;
}

export function Key(props: JSX.ButtonHTMLAttributes<HTMLButtonElement> & { legend?: string; symbol?: JSX.Element; primary?: boolean; latched?: boolean; lamp?: 'off' | 'green' | 'red' | 'warn'; size?: 'm' | 'kiosk' }) {
  const [local, rest] = splitProps(props, ['legend', 'symbol', 'primary', 'latched', 'lamp', 'size', 'class', 'children']);
  return (
    <div class="flex flex-col items-center gap-2">
      <Show when={local.lamp}><span class={`lamp ${local.lamp === 'off' ? '' : local.lamp}`} aria-hidden="true" /></Show>
      <button
        type="button"
        {...rest}
        aria-pressed={local.latched === undefined ? undefined : local.latched}
        class={`key ${local.primary ? 'primary' : ''} ${local.size === 'kiosk' ? 'h-[52px] min-w-[88px] text-lg' : 'h-12 min-w-[64px]'} px-4 ${local.class ?? ''}`}
      >
        <Show when={local.symbol} fallback={<span class="legend" style={{ color: 'inherit' }}>{local.legend}</span>}>
          <span>{local.symbol}</span>
          <Show when={local.legend}><span class="legend ml-2" style={{ color: 'inherit' }}>{local.legend}</span></Show>
        </Show>
        {local.children}
      </button>
    </div>
  );
}

/** Volume knob: the native range input carries focus, keyboard and value; the knob is a picture of it. */
export function Knob(props: { value: number; min?: number; max: number; ceiling?: number; label: string; onInput: (v: number) => void }) {
  const angle = () => -135 + 270 * ((props.value - (props.min ?? 0)) / Math.max(1, props.max - (props.min ?? 0)));
  return (
    <div class="flex flex-col items-center gap-2">
      <span class="legend">{props.label}</span>
      <div class="relative h-16 w-16 rounded-full" style={{ background: 'var(--knob-face)', 'box-shadow': 'var(--knob-raise)' }} aria-hidden="true">
        <div class="absolute left-1/2 top-[5px] h-[14px] w-[3px] -ml-[1.5px] rounded-sm"
          style={{ background: 'var(--lamp-red)', 'box-shadow': '0 0 6px rgba(255,68,54,0.8)', 'transform-origin': '50% 27px', transform: `rotate(${angle()}deg)` }} />
      </div>
      <input type="range" aria-label={props.label} min={props.min ?? 0} max={props.max} value={props.value} class="w-16"
        style={{ 'accent-color': 'var(--vfd)' }} onInput={(e) => props.onInput(Number(e.currentTarget.value))} />
    </div>
  );
}

/** Analogue VU needle. The face is drawn from VU_SCALE, the same mapping that turns the needle, so the printed
 *  numbers are where the needle really points. The needle element is exposed so the meter loop can write its
 *  transform directly each frame without re-rendering. */
const VU_PIVOT = { x: 80, y: 96 };
const vuPoint = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180;
  return { x: VU_PIVOT.x + r * Math.sin(a), y: VU_PIVOT.y - r * Math.cos(a) };
};
const vuArc = (from: number, to: number, r: number) => {
  const p = vuPoint(from, r);
  const q = vuPoint(to, r);
  return `M${p.x.toFixed(1)} ${p.y.toFixed(1)} A${r} ${r} 0 0 1 ${q.x.toFixed(1)} ${q.y.toFixed(1)}`;
};
const VU_LABELS = [-20, -10, -5, 0, 3];
const VU_MINOR = [-30, -15, -7, -3, -1, 1, 2];

export function VuMeter(props: { channel: 'L' | 'R'; needleRef: (el: SVGLineElement) => void; labelRef?: (el: SVGSVGElement) => void }) {
  const R = 64;
  const font = { 'font-family': 'var(--font-chassis)', 'font-weight': 600 } as const;
  return (
    <svg ref={props.labelRef} viewBox="0 0 160 104" role="img" aria-label={`${props.channel === 'L' ? 'Left' : 'Right'} channel level`} class="h-auto w-full">
      <path d={vuArc(-45, vuAngle(0), R)} fill="none" stroke="var(--vu-scale)" stroke-width="3" />
      <path d={vuArc(vuAngle(0), 45, R)} fill="none" stroke="var(--vu-scale-over)" stroke-width="3" />
      <For each={VU_MINOR}>{(db) => {
        const a = vuPoint(vuAngle(db), R + 1.5);
        const b = vuPoint(vuAngle(db), R + 5);
        return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={db > 0 ? 'var(--vu-scale-over)' : 'var(--vu-label)'} stroke-width="1" />;
      }}</For>
      <For each={VU_LABELS}>{(db) => {
        const a = vuPoint(vuAngle(db), R + 1.5);
        const b = vuPoint(vuAngle(db), R + 8);
        const t = vuPoint(vuAngle(db), R + 17);
        const over = db > 0;
        return <>
          <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={over ? 'var(--vu-scale-over)' : 'var(--legend)'} stroke-width="1.5" />
          <text x={t.x} y={t.y} text-anchor="middle" dominant-baseline="middle" font-size="13" {...font}
            fill={over ? 'var(--vu-label-over)' : 'var(--legend)'}>{db > 0 ? `+${db}` : db}</text>
        </>;
      }}</For>
      <line ref={props.needleRef} x1={VU_PIVOT.x} y1={VU_PIVOT.y} x2={VU_PIVOT.x} y2={VU_PIVOT.y - R - 4} stroke="var(--needle)" stroke-width="1.5" stroke-linecap="round"
        style={{ 'transform-origin': `${VU_PIVOT.x}px ${VU_PIVOT.y}px`, transform: `rotate(${vuAngle(-40)}deg)` }} />
      <circle cx={VU_PIVOT.x} cy={VU_PIVOT.y} r="4" fill="#2A2C31" stroke="#44474E" />
      <text x="6" y="100" font-size="18" {...font} fill="var(--legend)">{props.channel}</text>
      <text x="154" y="100" text-anchor="end" font-size="12" {...font} fill="var(--vu-label)">dB</text>
    </svg>
  );
}

/** Spectrum scale positions: a frequency's place on a log axis from 60 Hz to 16 kHz (0..1). */
export const hzAt = (hz: number) => Math.log(hz / 60) / Math.log(16000 / 60);

/** Segmented spectrum: bottom 7 green, 3 amber, top 2 red. Unlit segments are always drawn. */
export function Spectrum(props: { columns: number; segments?: number; colRef: (els: HTMLDivElement[]) => void; scale?: { label: string; at: number }[] }) {
  const segs = props.segments ?? 12;
  const cols: HTMLDivElement[] = [];
  const kind = (i: number) => (i >= segs - 2 ? 'r' : i >= segs - 5 ? 'a' : 'g');
  queueMicrotask(() => props.colRef(cols));
  return (
    <div class="flex h-full flex-col gap-1.5">
      <div class="flex min-h-[80px] flex-grow items-end gap-1">
        <For each={Array.from({ length: props.columns })}>
          {(_, c) => (
            <div class="spec-col" ref={(el) => (cols[c()] = el)}>
              <For each={Array.from({ length: segs })}>{(_, i) => <div class={`spec-seg ${kind(i())}`} />}</For>
            </div>
          )}
        </For>
      </div>
      <Show when={props.scale}>
        {/* each label sits under the frequency it names; the end labels align to the edges */}
        <div class="relative h-[15px] text-[12px] font-semibold leading-none tracking-[0.04em]" style={{ "font-family": "var(--font-chassis)", color: "var(--legend)" }}>
          <For each={props.scale}>{(s) => (
            <span class="absolute top-0 whitespace-nowrap"
              style={s.at <= 0 ? { left: '0' } : s.at >= 1 ? { right: '0' } : { left: `${s.at * 100}%`, transform: 'translateX(-50%)' }}>{s.label}</span>
          )}</For>
        </div>
      </Show>
    </div>
  );
}
